'use client'

import * as React from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'

export interface DateTimePickerProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
  name: string
  id?: string
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  required?: boolean
}

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>(
  (
    {
      name,
      id,
      value,
      onChange,
      disabled = false,
      placeholder = 'Select date and time',
      required = false,
      className,
      ...props
    },
    ref,
  ) => {
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>()
    const [selectedHour, setSelectedHour] = React.useState<string>('12')
    const [selectedMinute, setSelectedMinute] = React.useState<string>('00')
    const [selectedPeriod, setSelectedPeriod] = React.useState<'AM' | 'PM'>('PM')
    const [isOpen, setIsOpen] = React.useState(false)

    // Parse initial value if provided (24-hour format to 12-hour format)
    React.useEffect(() => {
      if (value) {
        try {
          const [dateStr, timeStr] = value.split(' ')
          if (dateStr && timeStr) {
            const [year, month, day] = dateStr.split('-').map(Number)
            const [hourStr, minute] = timeStr.split(':')
            const hour24 = Number.parseInt(hourStr, 10)

            if (year && month && day) {
              setSelectedDate(new Date(year, month - 1, day))
            }

            // Convert 24-hour to 12-hour format
            if (!Number.isNaN(hour24)) {
              const period = hour24 >= 12 ? 'PM' : 'AM'
              let hour12 = hour24 % 12
              if (hour12 === 0) hour12 = 12

              setSelectedHour(hour12.toString().padStart(2, '0'))
              setSelectedPeriod(period)
            }

            if (minute) setSelectedMinute(minute.padStart(2, '0'))
          }
        } catch (error) {
          console.error('Failed to parse date-time value:', error)
        }
      }
    }, [value])

    // Generate formatted datetime value (convert 12-hour to 24-hour format)
    const formattedDateTime = React.useMemo(() => {
      if (!selectedDate) return ''

      const dateStr = format(selectedDate, 'yyyy-MM-dd')

      // Convert 12-hour to 24-hour format
      let hour24 = Number.parseInt(selectedHour, 10)
      if (selectedPeriod === 'PM' && hour24 !== 12) {
        hour24 += 12
      } else if (selectedPeriod === 'AM' && hour24 === 12) {
        hour24 = 0
      }

      const hour24Str = hour24.toString().padStart(2, '0')
      return `${dateStr} ${hour24Str}:${selectedMinute}:00`
    }, [selectedDate, selectedHour, selectedMinute, selectedPeriod])

    // Notify parent of changes
    React.useEffect(() => {
      if (formattedDateTime && onChange) {
        onChange(formattedDateTime)
      }
    }, [formattedDateTime, onChange])

    // Get today's date in IST Colombo timezone (UTC+5:30)
    const todayIST = React.useMemo(() => {
      const now = new Date()
      const utc = now.getTime() + now.getTimezoneOffset() * 60000
      const istTime = new Date(utc + 5.5 * 60 * 60000)
      istTime.setHours(0, 0, 0, 0)
      return istTime
    }, [])

    // Handle date selection
    const handleDateSelect = (date: Date | undefined) => {
      setSelectedDate(date)
      setIsOpen(false)
    }

    // Generate hour options (01-12 for 12-hour format)
    const hours = React.useMemo(
      () => Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')),
      [],
    )

    // Generate minute options (00, 15, 30, 45)
    const minutes = React.useMemo(() => ['00', '15', '30', '45'], [])

    // AM/PM options
    const periods = React.useMemo(() => ['AM', 'PM'] as const, [])

    return (
      <div
        ref={ref}
        id={id}
        className={cn('flex items-center gap-2', className)}
        {...props}
      >
        {/* Date Picker Button */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="default"
              disabled={disabled}
              className={cn(
                'flex-1 justify-start text-left font-normal',
                !selectedDate && 'text-gray-500',
              )}
            >
              <CalendarIcon className="h-4 w-4" />
              {selectedDate ? format(selectedDate, 'MMM d, yyyy') : placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 z-[100]">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              disabled={disabled ? true : (date) => date < todayIST}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Hour Select */}
        <Select
          value={selectedHour}
          onValueChange={setSelectedHour}
          disabled={disabled}
        >
          <SelectTrigger className="w-20 h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]">
            {hours.map((hour) => (
              <SelectItem key={hour} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Time Separator */}
        <span className="text-gray-500 dark:text-gray-400">:</span>

        {/* Minute Select */}
        <Select
          value={selectedMinute}
          onValueChange={setSelectedMinute}
          disabled={disabled}
        >
          <SelectTrigger className="w-20 h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]">
            {minutes.map((minute) => (
              <SelectItem key={minute} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM Select */}
        <Select
          value={selectedPeriod}
          onValueChange={(value) => setSelectedPeriod(value as 'AM' | 'PM')}
          disabled={disabled}
        >
          <SelectTrigger className="w-20 h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[100]">
            {periods.map((period) => (
              <SelectItem key={period} value={period}>
                {period}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Hidden Input for Form Submission */}
        <input
          type="hidden"
          name={name}
          value={formattedDateTime}
          required={required}
        />
      </div>
    )
  },
)

DateTimePicker.displayName = 'DateTimePicker'

export { DateTimePicker }
